import React from 'react';
import {Link} from 'react-router-dom';
import {NAVY, TEXTE, TEXTE_SECONDAIRE} from '../tokens.js';

// Lot 11 : cascade « attendu -> causes -> résidu -> réel » en SVG maison (barres horizontales flottantes sur une échelle commune).
// `etapes` : [{cle, libelle, kg, nbPav}] (selectors/variation.js `cascade`). Attendu et réel partent de 0 (navy) ; chaque étape
// intermédiaire flotte de son cumul au cumul suivant : rouge pour une perte, vert pour un gain, gris pour le résidu non expliqué.
// `lien(etape)` : adresse de la carte des bornes de la cause (null = barre non cliquable).

const LARGEUR = 760;
const LIGNE = 32;
const HAUT = 18;
const X_LIBELLE = 0;
const X_BARRES = 230;
const X_FIN = 650;
const ROUGE = '#B22222';
const VERT = '#5C8316';
const GRIS = '#9AA2AC';

const tonnes = (kg) => `${(Math.abs(kg) / 1000).toFixed(1).replace('.', ',')} t`;
const signee = (kg) => `${kg < 0 ? '−' : '+'}${tonnes(kg)}`;

export default function Cascade({etapes, lien = () => null, titre = 'Cascade du tonnage'}) {
    // Cumuls : [debut, fin] de chaque barre.
    let cumul = 0;
    const barres = etapes.map((e) => {
        if (e.cle === 'attendu') { cumul = e.kg; return {...e, de: 0, a: e.kg}; }
        if (e.cle === 'reel') return {...e, de: 0, a: e.kg};
        const de = cumul; cumul += e.kg;
        return {...e, de, a: cumul};
    });
    const bornes = barres.flatMap((b) => [b.de, b.a]);
    const min = Math.min(0, ...bornes);
    const max = Math.max(1, ...bornes);
    const x = (v) => X_BARRES + ((v - min) / (max - min)) * (X_FIN - X_BARRES);
    const hauteur = barres.length * LIGNE + 8;

    return (
        <svg viewBox={`0 0 ${LARGEUR} ${hauteur}`} width="100%" role="group" aria-label={titre} style={{display: 'block', maxWidth: LARGEUR}}>
            {barres.map((b, i) => {
                const y = 4 + i * LIGNE;
                const extreme = b.cle === 'attendu' || b.cle === 'reel';
                const couleur = extreme ? NAVY : b.cle === 'residu' ? GRIS : b.kg < 0 ? ROUGE : VERT;
                const g0 = Math.min(x(b.de), x(b.a));
                const largeur = Math.max(2, Math.abs(x(b.a) - x(b.de)));
                const adresse = lien(b);
                const detail = b.nbPav > 0 ? `${b.nbPav} borne${b.nbPav > 1 ? 's' : ''}` : '';
                const valeur = extreme ? tonnes(b.kg) : signee(b.kg);
                const ligne = (
                    <g data-cle={b.cle} style={adresse ? {cursor: 'pointer'} : undefined}>
                        <rect x={0} y={y - 2} width={LARGEUR} height={LIGNE - 2} fill={adresse ? 'rgba(33,48,103,.04)' : 'transparent'} rx={6}/>
                        <text x={X_LIBELLE + 6} y={y + HAUT / 2 + 2} fontSize={13} fontWeight={extreme ? 700 : 600} fill={TEXTE}>{b.libelle}</text>
                        {detail && <text x={X_LIBELLE + 6} y={y + HAUT / 2 + 14} fontSize={11} fill={TEXTE_SECONDAIRE}>{detail}</text>}
                        <rect x={g0} y={y + 1} width={largeur} height={HAUT} rx={3} fill={couleur}/>
                        <text x={X_FIN + 12} y={y + HAUT / 2 + 5} fontSize={13} fontWeight={700} fill={extreme ? NAVY : couleur} style={{fontVariantNumeric: 'tabular-nums'}}>{valeur}</text>
                    </g>
                );
                return adresse
                    ? <Link key={b.cle} to={adresse} aria-label={`${b.libelle} : ${valeur}${detail ? `, ${detail}` : ''}. Voir les bornes sur la carte`}>{ligne}</Link>
                    : <g key={b.cle} role="img" aria-label={`${b.libelle} : ${valeur}`}>{ligne}</g>;
            })}
        </svg>
    );
}
